import {
  Activation,
  ApiError,
  ApiResponse,
  MetaCategory,
  MetaInterest,
  MetaRecipe,
  MetaSize,
  MetaSizeRequest,
} from "@/types";

import { AxiosRequestConfig } from "axios";
import { fetchData } from "@/utils";

type GetActivationsApiResponse = {
  data: Activation[];
  error: ApiError | null;
};

export const getActivations = async (
  accessToken: string,
  client: string
): Promise<GetActivationsApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/activation?client=${client}`;

  const config: AxiosRequestConfig = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

type GetActivationApiResponse = {
  data: Activation;
  error: ApiError | null;
};

export const getActivation = async (
  accessToken: string,
  campaignId: string
): Promise<GetActivationApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/activation/${campaignId}`;

  const config: AxiosRequestConfig = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

type BuildActivationApiResponse = {
  data: { activations: Activation[]; activation: Activation };
  error: ApiError | null;
};

export const buildActivation = async (
  accessToken: string,
  activation: Activation,
  campaignType: string
): Promise<BuildActivationApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/activation/${campaignType}`;

  const config: AxiosRequestConfig = {
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    data: activation,
  };

  return fetchData(config);
};

export const deleteActivation = async (
  accessToken: string,
  campaign_id: string,
  campaign_name: string,
  campaign_type: string,
  client: string
): Promise<GetActivationsApiResponse> => {
  const url = `${process.env.REACT_APP_API_URL}/v2/activation`;

  const config: AxiosRequestConfig = {
    method: "DELETE",
    url,
    params: {
      campaign_id,
      campaign_name,
      campaign_type,
      client,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

type GetActivationRecipeApiResponse = {
  data: {
    activation: Activation;
    categories: MetaCategory[];
    interests: MetaInterest[];
  };
  error: ApiError | null;
};

export const getActivationRecipe = async (
  accessToken: string,
  campaignId: string
): Promise<GetActivationRecipeApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/activation/${campaignId}/recipe`;

  const config: AxiosRequestConfig = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

type SaveActivationRecipeApiResponse = {
  data: MetaInterest[];
  error: ApiError | null;
};

export const saveActivationRecipe = async (
  accessToken: string,
  campaignId: string,
  recipe: MetaRecipe
): Promise<SaveActivationRecipeApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/activation/${campaignId}/recipe`;

  const config: AxiosRequestConfig = {
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: recipe,
  };

  return fetchData(config);
};

type SearchMetaApiResponse = {
  data: MetaInterest[];
  error: ApiError | null;
};

export const searchMeta = async (
  accessToken: string,
  searchTerm: string
): Promise<SearchMetaApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/activation/search/meta?search_term=${searchTerm}`;

  const config: AxiosRequestConfig = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  };

  return fetchData(config);
};

type SizeMetaApiResponse = {
  data: MetaSize;
  error: ApiError | null;
};

export const sizeMeta = async (
  accessToken: string,
  targetSpec: MetaSizeRequest
): Promise<SizeMetaApiResponse> => {
  let url = `${process.env.REACT_APP_API_URL}/v2/activation/size/meta`;

  const config: AxiosRequestConfig = {
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: targetSpec,
  };

  return fetchData(config);
};

export const shareActivation = async (
  accessToken: string,
  campaignId: string,
  userIds: number[]
): Promise<ApiResponse<{ message: string }>> => {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: `${process.env.REACT_APP_API_URL}/v2/activation/share/${campaignId}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    data: { users: userIds },
  };

  return fetchData(config);
};
